import { get, send } from "./api.js";
import type { SchoolBillingHold } from "@wmf/shared/schoolInvoices";

/**
 * School invoicing, from the browser's side.
 *
 * Amounts stay strings all the way through. They arrive from the database as
 * `numeric(10,2)` and are only ever displayed or echoed back, so parsing them
 * into JavaScript numbers would add a rounding step for no gain — and the one
 * place a total is compared, `expected_total` on issue, must be compared as
 * the server wrote it.
 */

export type SchoolInvoiceStatus = "draft" | "issued" | "paid" | "void";

export interface SchoolListPage {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface SchoolInvoiceLine {
  id?: string;
  studentName: string;
  description: string;
  lessonDate: string;
  amount: string;
}

export interface SchoolInvoiceSummary {
  id: string;
  reference: string;
  parentUserId: string;
  parentEmail: string | null;
  periodStart: string;
  periodEnd: string;
  status: SchoolInvoiceStatus;
  totalAmount: string;
  /** Credit taken off at issue. "0.00" when there was none. */
  creditApplied: string;
  dueOn: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  /** The family says they have paid. Not payment — something to go and check. */
  parentDeclaredPaidAt: string | null;
  parentDeclaredNote: string | null;
}

/** A school pupil as the invoicing tick list shows them. */
export interface BillablePupil {
  id: string;
  fullName: string;
  schoolName: string;
  lessonRate: string | null;
  families: { parentUserId: string; email: string | null }[];
}

export interface BatchPreview {
  billingHolds?: SchoolBillingHold[];
  families: {
    parentUserId: string;
    email: string | null;
    lineCount: number;
    total: string;
    skipped: SkippedLessons[];
    /** Everything this family has waiting. */
    availableCredit: string;
    /** What would actually come off this bill — a credit bigger than the bill
     * is held back rather than partly used, so the two can differ. */
    creditToApply: string;
    toPay: string;
  }[];
  totals: { families: number; lessons: number; amount: string; credit: string };
  shared: { studentId: string; studentName: string; billedTo: string; alsoLinkedTo: string[] }[];
  unlinked: { studentId: string; fullName: string }[];
}

export interface BatchResult {
  issued: {
    parentUserId: string;
    invoiceId: string;
    reference: string;
    total: string;
    creditApplied: string;
    toPay: string;
  }[];
  failed: { parentUserId: string; reason: string }[];
  unlinked: string[];
  /** Delivery is a separate outcome from invoice creation and must be visible. */
  emailed: number;
  withoutAddress: number;
  emailFailed: number;
}

export async function getBillablePupils(): Promise<{ students: BillablePupil[] }> {
  return get("/school-invoices/students");
}

export async function previewSchoolInvoiceBatch(input: {
  studentIds: string[];
  periodStart: string;
  periodEnd: string;
}): Promise<BatchPreview> {
  return send("POST", "/school-invoices/preview-batch", {
    student_ids: input.studentIds,
    period_start: input.periodStart,
    period_end: input.periodEnd,
  });
}

export async function issueSchoolInvoiceBatch(input: {
  studentIds: string[];
  expectedTotals: Record<string, string>;
  periodStart: string;
  periodEnd: string;
  dueOn?: string;
  notes?: string;
  applyCredit: boolean;
}): Promise<BatchResult> {
  return send("POST", "/school-invoices/batch", {
    student_ids: input.studentIds,
    expected_totals: input.expectedTotals,
    period_start: input.periodStart,
    period_end: input.periodEnd,
    apply_credit: input.applyCredit,
    ...(input.dueOn ? { due_on: input.dueOn } : {}),
    ...(input.notes ? { notes: input.notes } : {}),
  });
}

// ── Credits ──────────────────────────────────────────────────────────────────

export type CreditKind = "make_up" | "money";
export type CreditStatus = "open" | "settled" | "cancelled";

export interface SchoolCredit {
  id: string;
  kind: CreditKind;
  status: CreditStatus;
  amount: string;
  reason: string;
  studentId: string | null;
  schoolPupilId?: string | null;
  schoolSessionId?: string | null;
  studentName: string;
  parentUserId: string | null;
  parentEmail: string | null;
  createdAt: string;
  settledAt: string | null;
  settledNote: string | null;
  appliedToInvoiceId: string | null;
  originInvoiceVoided?: boolean;
}

/**
 * School credit reads use the complete read projection rather than the old
 * default-open, capped endpoint. Omitting `params` preserves the common
 * outstanding-only read; passing an object without `status` means all statuses,
 * which is what the existing "show settled" toggle has always intended.
 */
export async function listCredits(params?: {
  status?: CreditStatus | "all" | "history";
  kind?: CreditKind;
  page?: number;
  limit?: number;
  pupilId?: string;
}): Promise<{
  credits: SchoolCredit[];
  current?: SchoolCredit[];
  history?: SchoolCredit[];
  historyPage?: number;
  historyLimit?: number;
  historyTotal?: number;
  historyPages?: number;
  page?: number;
  limit?: number;
  total?: number;
  pages?: number;
}> {
  return get("/school-invoices/credits-history", {
    status: params === undefined ? "open" : (params.status ?? "all"),
    kind: params?.kind,
    pupilId: params?.pupilId,
    page: params?.page === undefined ? undefined : String(params.page),
    limit: params?.limit === undefined ? undefined : String(params.limit),
  });
}

export async function createCredit(input: {
  parentUserId?: string;
  studentId: string;
  kind: CreditKind;
  amount: string;
  reason: string;
  appointmentId?: string;
  originInvoiceId?: string;
}): Promise<void> {
  await send("POST", "/school-invoices/credits", {
    parent_user_id: input.parentUserId,
    student_id: input.studentId,
    kind: input.kind,
    amount: input.amount,
    reason: input.reason,
    ...(input.appointmentId ? { appointment_id: input.appointmentId } : {}),
    ...(input.originInvoiceId ? { origin_invoice_id: input.originInvoiceId } : {}),
  });
}

/** The make-up lesson happened. */
export async function markCreditTaught(creditId: string, note?: string): Promise<void> {
  await send("POST", `/school-invoices/credits/${encodeURIComponent(creditId)}/taught`, {
    ...(note ? { note } : {}),
  });
}

/** The make-up never happened, so give the money instead. */
export async function convertCreditToMoney(creditId: string): Promise<void> {
  await send("POST", `/school-invoices/credits/${encodeURIComponent(creditId)}/convert`, {});
}

export async function cancelCredit(creditId: string, note?: string): Promise<void> {
  await send("POST", `/school-invoices/credits/${encodeURIComponent(creditId)}/cancel`, {
    ...(note ? { note } : {}),
  });
}

export interface BillableFamily {
  parentUserId: string;
  email: string | null;
  username: string | null;
  children: { id: string; fullName: string; lessonRate: string | null }[];
}

/** Why a lesson in the period is not on the bill. */
export interface SkippedLessons {
  studentId: string;
  studentName: string;
  reason: "no-rate" | "already-billed";
  count: number;
}

export interface InvoicePreview {
  billingHolds?: SchoolBillingHold[];
  lines: (SchoolInvoiceLine & { appointmentId: string; studentId: string })[];
  total: string;
  skipped: SkippedLessons[];
}

export const SKIPPED_REASON_LABELS: Record<SkippedLessons["reason"], string> = {
  "no-rate": "no lesson rate set",
  "already-billed": "already on another invoice",
};

export async function getBillableFamilies(): Promise<{ families: BillableFamily[] }> {
  return get("/school-invoices/families");
}

export async function previewSchoolInvoice(input: {
  parentUserId: string;
  periodStart: string;
  periodEnd: string;
}): Promise<InvoicePreview> {
  return send("POST", "/school-invoices/preview", {
    parent_user_id: input.parentUserId,
    period_start: input.periodStart,
    period_end: input.periodEnd,
  });
}

export async function issueSchoolInvoice(input: {
  parentUserId: string;
  periodStart: string;
  periodEnd: string;
  expectedTotal: string;
  notes?: string;
}): Promise<{ invoice: { id: string; reference: string } }> {
  return send("POST", "/school-invoices", {
    parent_user_id: input.parentUserId,
    period_start: input.periodStart,
    period_end: input.periodEnd,
    expected_total: input.expectedTotal,
    ...(input.notes ? { notes: input.notes } : {}),
  });
}

export async function listSchoolInvoices(params?: {
  status?: SchoolInvoiceStatus;
  school?: string;
  family?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}): Promise<{
  invoices: SchoolInvoiceSummary[];
  page?: number;
  limit?: number;
  total?: number;
  pages?: number;
  totals?: {
    count: number;
    totalAmount: string;
    outstandingAmount: string;
    overdueCount: number;
    claimedCount: number;
  };
}>;

export async function listSchoolInvoices(
  status?: SchoolInvoiceStatus,
): Promise<{
  invoices: SchoolInvoiceSummary[];
  page?: number;
  limit?: number;
  total?: number;
  pages?: number;
  totals?: {
    count: number;
    totalAmount: string;
    outstandingAmount: string;
    overdueCount: number;
    claimedCount: number;
  };
}>;

export async function listSchoolInvoices(
  statusOrParams?: SchoolInvoiceStatus | {
    status?: SchoolInvoiceStatus;
    school?: string;
    family?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  },
): Promise<{
  invoices: SchoolInvoiceSummary[];
  page?: number;
  limit?: number;
  total?: number;
  pages?: number;
  totals?: {
    count: number;
    totalAmount: string;
    outstandingAmount: string;
    overdueCount: number;
    claimedCount: number;
  };
}> {
  const params = typeof statusOrParams === "string" ? { status: statusOrParams } : statusOrParams;
  return get("/school-invoices", params
    ? {
        ...params,
        page: params.page === undefined ? undefined : String(params.page),
        limit: params.limit === undefined ? undefined : String(params.limit),
      }
    : undefined);
}

export async function getSchoolInvoice(
  invoiceId: string,
): Promise<{ invoice: SchoolInvoiceSummary & { notes: string | null; paymentNote: string | null }; lines: SchoolInvoiceLine[] }> {
  return get(`/school-invoices/${encodeURIComponent(invoiceId)}`);
}

export async function markSchoolInvoicePaid(
  invoiceId: string,
  paymentNote?: string,
): Promise<void> {
  await send("POST", `/school-invoices/${encodeURIComponent(invoiceId)}/paid`, {
    ...(paymentNote ? { payment_note: paymentNote } : {}),
  });
}

/**
 * Send a family their invoice again.
 *
 * The ordinary send is deliberately unrepeatable — its dedupe key names the
 * invoice — so this is the only way to produce a second copy, and it exists
 * because "we never got it" is the first thing a parent says.
 */
export async function resendSchoolInvoice(invoiceId: string): Promise<void> {
  await send("POST", `/school-invoices/${encodeURIComponent(invoiceId)}/resend`, {});
}

export async function voidSchoolInvoice(invoiceId: string, reason?: string): Promise<void> {
  await send(
    "POST",
    `/school-invoices/${encodeURIComponent(invoiceId)}/void`,
    reason?.trim() ? { reason } : {},
  );
}

export async function getOutstandingTotal(): Promise<{
  outstanding: { count: number; total: string };
}> {
  return get("/school-invoices/summary/outstanding");
}

// ── The family's own view ────────────────────────────────────────────────────

export interface FamilyInvoice {
  id: string;
  reference: string;
  periodStart: string;
  periodEnd: string;
  status: SchoolInvoiceStatus;
  totalAmount: string;
  creditApplied: string;
  dueOn: string | null;
  notes: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  /** When this family said they had paid, if they have. */
  parentDeclaredPaidAt: string | null;
  lines: SchoolInvoiceLine[];
}

/**
 * Tell the school the transfer has gone.
 *
 * A claim, not a payment: it asks the school to check the bank. The tick on
 * the invoice comes from the school confirming it, never from this.
 */
export async function declareInvoicePaid(invoiceId: string, note?: string): Promise<void> {
  await send("POST", `/me/invoices/${encodeURIComponent(invoiceId)}/declare-paid`, {
    ...(note ? { note } : {}),
  });
}

export async function getFamilyInvoices(params: {
  historyPage?: number;
  historyLimit?: number;
} = {}): Promise<{
  invoices: FamilyInvoice[];
  payTo: string | null;
  historyPage?: number;
  historyLimit?: number;
  historyHasMore?: boolean;
}> {
  return get("/me/invoices", {
    historyPage: params.historyPage === undefined ? undefined : String(params.historyPage),
    historyLimit: params.historyLimit === undefined ? undefined : String(params.historyLimit),
  });
}
